import { useGetBankAccount } from '@/apis/bank-accounts/hooks/useGetBankAccount'
import AppAlertComponent from '@/app/common/components/AppAlertComponent/AppAlertComponent'
import { buttonVariants } from '@/app/common/components/Button'
import PageLoader from '@/app/common/components/PageLoader/PageLoader'
import ApplicationStepper, {
  ApplicationStep,
} from '@/app/pages/family_application/ApplicationStepper'
import { BackIcon } from '@/common/icons/general.tsx'
import { cn } from '@/common/utilities.ts'
import { AppRoutePaths } from '@/routing/route_paths'
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

export const ReimbursementStep = {
  ADD_DETAILS: 'ADD_DETAILS',
  CAPTURE_IMAGES: 'CAPTURE_IMAGES',
  CHOOSE_PURSE: 'CHOOSE_PURSE',
  SELECT_CATEGORIES: 'SELECT_CATEGORIES',
  REVIEW: 'REVIEW',
  ALLOCATE_CATEGORIES: 'ALLOCATE_CATEGORIES',
  COMPLETE: 'COMPLETE',
} as const

// Type for type safety (optional but recommended)
export type ReimbursementStep =
  (typeof ReimbursementStep)[keyof typeof ReimbursementStep]

// Define the mapping between ReimbursementStep enum and stepper display
type NewReimbursementStepperItem = ApplicationStep & {
  reimbursementStep: ReimbursementStep
}

// Define all steps in order for the stepper display
const NewReimbursementSteps: NewReimbursementStepperItem[] = [
  {
    name: 'add_details',
    code: 'addDetails',
    title_translation_key: 'add_details',
    location: '',
    reimbursementStep: ReimbursementStep.ADD_DETAILS,
  },
  {
    name: 'capture_images',
    code: 'captureImages',
    location: '',
    title_translation_key: 'upload_images',
    reimbursementStep: ReimbursementStep.CAPTURE_IMAGES,
  },
  {
    name: 'choose_purse',
    code: 'choosePurse',
    location: '',
    title_translation_key: 'choose_purse',
    reimbursementStep: ReimbursementStep.CHOOSE_PURSE,
  },
  {
    name: 'select_categories',
    code: 'selectCategories',
    location: '',
    title_translation_key: 'select_categories',
    reimbursementStep: ReimbursementStep.SELECT_CATEGORIES,
  },
  {
    name: 'allocate_categories',
    code: 'allocateCategories',
    location: '',
    title_translation_key: 'allocate_categories',
    reimbursementStep: ReimbursementStep.ALLOCATE_CATEGORIES,
  },
  {
    name: 'review',
    code: 'review',
    location: '',
    title_translation_key: 'review',
    reimbursementStep: ReimbursementStep.REVIEW,
  },
]

export const NewReimbursementLayout = ({
  children,
  onBack,
  currentStep,
}: {
  currentStep: ReimbursementStep
  children: React.ReactNode
  onBack: () => void
}) => {
  const { t: translate } = useTranslation()

  const bankAccountQuery = useGetBankAccount()
  const programSettings = useSelector(selectProgramSettings)

  // Find the current step index in our stepper array
  const currentStepIndex = NewReimbursementSteps.findIndex(
    (step) => step.reimbursementStep === currentStep
  )

  // Get all completed step codes
  const completedSteps = NewReimbursementSteps.filter(
    (_, index) => index < currentStepIndex
  ).map((step) => step.code)

  // Check if the back button should be disabled (first step or complete step)
  const disableBackButton =
    currentStep === ReimbursementStep.ADD_DETAILS ||
    currentStep === ReimbursementStep.COMPLETE

  return (
    <PageLoader query={bankAccountQuery}>
      {(bankAccount) => {
        const hasNotAddedBankAccount = Object.keys(bankAccount).length === 0
        if (
          hasNotAddedBankAccount &&
          programSettings.require_family_bank_info_for_reimbursement == 1
        ) {
          return (
            <div className="max-w-md mx-auto">
              <AppAlertComponent
                variant="warning"
                title="Bank account required"
                text="To proceed with the reimbursement process, please add your bank account details."
              >
                <Link
                  className={buttonVariants({
                    variant: 'primary',
                  })}
                  to={AppRoutePaths.reimbursements.accounts.link.address}
                >
                  Add bank account
                </Link>
              </AppAlertComponent>
            </div>
          )
        }

        return (
          <div className={cn('flex-grow flex flex-col')}>
            <div className="flex items-center mb-4 lg:mb-8">
              <button
                type="button"
                onClick={onBack}
                aria-label={translate('back')}
                disabled={disableBackButton}
                className={
                  disableBackButton ? 'opacity-50 cursor-not-allowed' : ''
                }
              >
                <BackIcon className="w-6 h-6" />
              </button>

              <ApplicationStepper
                completeSteps={completedSteps}
                currentStepIndex={currentStepIndex}
                steps={NewReimbursementSteps}
                className={cn('grow flex justify-center')}
              />

              {/* Empty div for layout balance */}
              <div className="w-6"></div>
            </div>

            <div
              className={cn('flex-grow flex flex-col max-w-xl w-full mx-auto')}
            >
              {children}
            </div>
          </div>
        )
      }}
    </PageLoader>
  )
}
